// vg-binding: CONTEXT:decisions  (P999.11.D-09/D-14/D-15/D-16 tokens + D-04 dark-ok)
// vg-binding: INTERFACE-STANDARDS:error-shape  (pure FE restyle — no API surface changed)
// apps/admin/src/pages/settings/ChangePasswordPage.tsx
//
// Phase 1 Task 47 — Change password (sensitive route, settings area).
//
// Per CONTEXT + SPECS / AC-08:
//   - currentPassword + newPassword + confirm form, policy live-checked.
//   - POST /api/v1/auth/change-password.
//   - On 401 with envelope code `AUTH_PERM_VERSION_STALE` → call
//     useAuth.refresh and retry the call exactly once. the
//     server bumps `permVersion` on sensitive operations; refresh re-binds
//     the JWT so the second attempt clears the stale check.

import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Form,
  FormField,
  FormLabel,
  FormMessage,
  Input,
  useAuth,
  SettingsLayout,
} from '@printway/ui';
import { apiClient } from '@printway/shared/api';

import { useTranslation } from '../../i18n/useTranslation.js';

// ---------------------------------------------------------------------------
// Password policy — mirrors @printway/zod-schemas PasswordSchema.
// ---------------------------------------------------------------------------

const SPECIAL_CHARS_RE = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`]/;

interface PolicyCheck {
  id: string;
  labelKey: string;
  test: (pw: string) => boolean;
}

const PASSWORD_RULES: readonly PolicyCheck[] = [
  { id: 'len', labelKey: 'changePassword.rules.len', test: (pw) => pw.length >= 12 },
  {
    id: 'upper',
    labelKey: 'changePassword.rules.upper',
    test: (pw) => /[A-Z]/.test(pw),
  },
  { id: 'num', labelKey: 'changePassword.rules.num', test: (pw) => /[0-9]/.test(pw) },
  {
    id: 'special',
    labelKey: 'changePassword.rules.special',
    test: (pw) => SPECIAL_CHARS_RE.test(pw),
  },
];

type Translate = (key: string, vars?: Record<string, unknown>) => string;

function buildChangePasswordSchema(t: Translate) {
  return z
    .object({
      currentPassword: z.string().min(1, t('changePassword.validation.currentRequired')),
      newPassword: z
        .string()
        .min(12, t('changePassword.validation.newMin'))
        .refine((p) => /[A-Z]/.test(p), t('changePassword.validation.newUpper'))
        .refine((p) => /[0-9]/.test(p), t('changePassword.validation.newNum'))
        .refine(
          (p) => SPECIAL_CHARS_RE.test(p),
          t('changePassword.validation.newSpecial'),
        ),
      confirmPassword: z.string().min(1, t('changePassword.validation.confirmRequired')),
    })
    .refine((d) => d.newPassword === d.confirmPassword, {
      message: t('changePassword.validation.mismatch'),
      path: ['confirmPassword'],
    })
    .refine((d) => d.currentPassword !== d.newPassword, {
      message: t('changePassword.validation.sameAsCurrent'),
      path: ['newPassword'],
    });
}
interface ChangePasswordInput {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

// ---------------------------------------------------------------------------
// Envelope helpers — narrow shape returned by /api/v1/auth/change-password.
// ---------------------------------------------------------------------------

interface ErrorEnvelope {
  success: false;
  error: { code: string; message: string };
}

function extractErrorCode(payload: unknown): string | null {
  if (
    payload &&
    typeof payload === 'object' &&
    'success' in payload &&
    (payload as ErrorEnvelope).success === false &&
    'error' in payload
  ) {
    return (payload as ErrorEnvelope).error.code ?? null;
  }
  return null;
}

interface PostResult {
  ok: boolean;
  status: number;
  errorCode: string | null;
}

async function postChangePassword(body: {
  currentPassword: string;
  newPassword: string;
}): Promise<PostResult> {
  const client = apiClient();
  const { response, error } = await (
    client as unknown as {
      POST: (
        path: string,
        init: { body: unknown },
      ) => Promise<{
        data?: unknown;
        error?: unknown;
        response: Response;
      }>;
    }
  ).POST('/api/v1/auth/change-password', { body });
  return {
    ok: response.ok,
    status: response.status,
    errorCode: extractErrorCode(error),
  };
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type ServerErrorState =
  | null
  | { kind: 'invalid' }
  | { kind: 'reused' }
  | { kind: 'generic'; message: string };

export function ChangePasswordPage(): JSX.Element {
  const { t } = useTranslation('settings');
  const refresh = useAuth((s) => s.refresh);
  const [serverError, setServerError] = useState<ServerErrorState>(null);
  const [submitted, setSubmitted] = useState(false);

  const schema = useMemo(() => buildChangePasswordSchema(t), [t]);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ChangePasswordInput>({
    resolver: zodResolver(schema as never),
    mode: 'onChange',
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const newPassword = watch('newPassword') ?? '';
  const checks = useMemo(
    () =>
      PASSWORD_RULES.map((rule) => ({
        id: rule.id,
        label: t(rule.labelKey),
        passed: rule.test(newPassword),
      })),
    [newPassword, t],
  );

  const onSubmit = async (data: ChangePasswordInput): Promise<void> => {
    setServerError(null);
    setSubmitted(false);
    const body = {
      currentPassword: data.currentPassword,
      newPassword: data.newPassword,
    };

    try {
      let res = await postChangePassword(body);

      //  Tier A — perm-version stale → refresh once + retry.
      if (
        !res.ok &&
        res.status === 401 &&
        res.errorCode === 'AUTH_PERM_VERSION_STALE'
      ) {
        await refresh();
        res = await postChangePassword(body);
      }

      if (res.ok) {
        setSubmitted(true);
        reset({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
        return;
      }

      if (res.errorCode === 'AUTH_PASSWORD_REUSED') {
        setServerError({ kind: 'reused' });
        return;
      }
      if (
        res.errorCode === 'AUTH_INVALID_CREDENTIALS' ||
        res.status === 401 ||
        res.status === 403
      ) {
        setServerError({ kind: 'invalid' });
        return;
      }
      setServerError({
        kind: 'generic',
        message:
          res.errorCode ??
          t('changePassword.errors.generic', { status: String(res.status) }),
      });
    } catch (err) {
      setServerError({
        kind: 'generic',
        message:
          err instanceof Error ? err.message : t('changePassword.errors.unknown'),
      });
    }
  };

  return (
    <SettingsLayout portal="admin" activeTab="security">
      <Card>
        <CardHeader>
          <CardTitle>{t('changePassword.card.title')}</CardTitle>
          <CardDescription>{t('changePassword.card.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <FormField>
              <FormLabel htmlFor="currentPassword">
                {t('changePassword.fields.current')}
              </FormLabel>
              <Input
                id="currentPassword"
                type="password"
                autoComplete="current-password"
                {...register('currentPassword')}
              />
              {errors.currentPassword ? (
                <FormMessage>{errors.currentPassword.message}</FormMessage>
              ) : null}
            </FormField>

            <FormField>
              <FormLabel htmlFor="newPassword">{t('changePassword.fields.new')}</FormLabel>
              <Input
                id="newPassword"
                type="password"
                autoComplete="new-password"
                {...register('newPassword')}
              />
              {errors.newPassword ? (
                <FormMessage>{errors.newPassword.message}</FormMessage>
              ) : null}
            </FormField>

            <ul className="space-y-1 text-xs" aria-label={t('changePassword.rulesAria')}>
              {checks.map((c) => (
                <li
                  key={c.id}
                  className={
                    c.passed
                      ? 'text-emerald-600 dark:text-emerald-300'
                      : 'text-pw-text-muted'
                  }
                >
                  <span aria-hidden="true">{c.passed ? '✓' : '○'}</span>{' '}
                  {c.label}
                </li>
              ))}
            </ul>

            <FormField>
              <FormLabel htmlFor="confirmPassword">
                {t('changePassword.fields.confirm')}
              </FormLabel>
              <Input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                {...register('confirmPassword')}
              />
              {errors.confirmPassword ? (
                <FormMessage>{errors.confirmPassword.message}</FormMessage>
              ) : null}
            </FormField>

            {serverError?.kind === 'invalid' ? (
              <FormMessage>{t('changePassword.errors.invalid')}</FormMessage>
            ) : null}
            {serverError?.kind === 'reused' ? (
              <FormMessage>{t('changePassword.errors.reused')}</FormMessage>
            ) : null}
            {serverError?.kind === 'generic' ? (
              <FormMessage>{serverError.message}</FormMessage>
            ) : null}
            {submitted ? (
              <p className="text-sm font-medium text-emerald-600 dark:text-emerald-300">
                {t('changePassword.success')}
              </p>
            ) : null}

            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting
                ? t('changePassword.buttons.updating')
                : t('changePassword.buttons.update')}
            </Button>
          </Form>
        </CardContent>
      </Card>
    </SettingsLayout>
  );
}

export default ChangePasswordPage;
